//libraries imports
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

//Core Components
import Loader from "../../../../core/common/loader";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { DatePicker, Input, Select } from "antd";
import { Dayjs } from "dayjs";

//Other Components
import StatsCard from "./components/StatsCard";
import Pagination from "../../common/components/Pagination";
import AllReportsCard from "./components/AllReportsCard";

// Helpers & Types
import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";
import { BackendAllReport } from "./types";
import {
  getEmployeeOptions,
  getFullName,
} from "../../reports-settings/report-type/helpers/form.handler";
import { toProperCase } from "../../../../helper/textHelper";
import QuickInfoModal from "../../common/components/QuickInfoModal";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { truncateCompanyText } from "../../../../helper/company_helper";
import CommonSelect from "../../../../core/common/commonSelect";
import { useSelector } from "react-redux";

// interfaces
interface AllReportsPaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  startIndex: number;
  endIndex: number;
}

interface AllReportsFilters {
  search_term: string | null;
  user_id: number | null;
  report_type_id: number | null;
  report_frequency: string | null;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  sort_by?: string | null;
  department_id?: number | null;
}

const DEFAULT_FILTERS: AllReportsFilters = {
  search_term: null,
  user_id: null,
  report_type_id: null,
  report_frequency: null,
  start_date: null,
  end_date: null,
  sort_by: null,
  department_id: null,
};

const TeamReports: React.FC = () => {
  // LOCAL STORAGE
  const signature: string | null = localStorage.getItem("signature");
  const user = useSelector((state: any) => state?.user?.user?.userDetails);
  const isAdmin = user?.role_char?.toLowerCase() == "admin";

  // STATE MANAGEMENT
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allReports, setAllReports] = useState<BackendAllReport[]>([]);
  const [stats, setStats] = useState<{ summary: any; frequency: any } | any>(
    {}
  );
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyOptions, setCompanyOptions] = useState<any[]>([]);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState({
    company_name: "Select Company",
    company_id: "",
    company_type: "Type",
  });

  // All reports - Backend pagination
  const [allReportsPaginationData, setAllReportsPaginationData] =
    useState<AllReportsPaginationData>({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPrevPage: false,
      startIndex: 1,
      endIndex: 0,
    });

  // All reports filters
  const [allReportsFilters, setAllReportsFilters] =
    useState<AllReportsFilters>(DEFAULT_FILTERS);

  // Filter states for All Reports
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<number | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState<number | null>(null);
  const [reportFrequency, setReportFrequency] = useState<string | null>(null);
  const [reportTypeFilter, setReportTypeFilter] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [reportTypes, setReportTypes] = useState<any>([]);
  const [allUsers, setAllUsers] = useState<any>([]);
  const [allDepartments, setAllDepartments] = useState<any>([]);
  const [reportsStartDate, setReportsStartDate] = useState<Dayjs | null>(null);
  const [reportsEndDate, setReportsEndDate] = useState<Dayjs | null>(null);
  const [reportsSortBy, setReportsSortBy] = useState<string | null>(null);

  const reportsSortOptions = [
    {
      value: "report_date-ASC",
      label: "Report Date [Old - New]",
    },
    {
      value: "report_date-DESC",
      label: "Report Date [New - Old]",
    },
    {
      value: "submitted_at-DESC",
      label: "Submission Date [New - Old]",
    },
    {
      value: "submitted_at-ASC",
      label: "Submission Date [Old - New]",
    },
    {
      value: "report_type_id-ASC",
      label: "Report Type [Ascending]",
    },
    {
      value: "report_type_id-DESC",
      label: "Report Type [DESC]",
    },
  ];

  // HANDLERS
  const activeFiltersCount = [
    searchTerm,
    userFilter,
    departmentFilter,
    reportFrequency,
    reportTypeFilter,
    reportsStartDate,
    reportsEndDate,
    reportsSortBy,
  ].filter((filter) => filter !== null).length;

  // Clear individual filters
  const clearSearchTerm = () => setSearchTerm(null);
  const clearUserFilter = () => setUserFilter(null);
  const clearDepartmentFilter = () => setDepartmentFilter(null);
  const clearReportFrequency = () => setReportFrequency(null);
  const clearReportTypeFilter = () => setReportTypeFilter(null);
  const clearStartDateFilters = () => {
    setReportsStartDate(null);
  };
  const clearEndDateFilters = () => {
    setReportsEndDate(null);
  };
  const clearSortBy = () => setReportsSortBy(null);

  // All Reports Filter Handlers
  const handleAllReportsSearch = async () => {
    const filters: AllReportsFilters = {
      search_term: searchTerm?.trim() || null,
      report_type_id: reportTypeFilter || null,
      user_id: userFilter || null,
      report_frequency: reportFrequency || null,
      start_date: reportsStartDate || null,
      end_date: reportsEndDate || null,
      sort_by: reportsSortBy,
      department_id: departmentFilter,
    };
    setAllReportsFilters(filters);
    await fetchData(1, filters);
  };

  const handleClearAllReportsFilters = async () => {
    setSearchTerm(null);
    setReportFrequency(null);
    setReportTypeFilter(null);
    setReportsStartDate(null);
    setReportsEndDate(null);
    setUserFilter(null);
    setDepartmentFilter(null);
    setReportsSortBy(null);
    setAllReportsFilters(DEFAULT_FILTERS);
    await fetchData(1, DEFAULT_FILTERS);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleCompanySelection = (
    selectedOption: any,
    setSelectedCompanyOption: any
  ) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption?.value?.split("-")[0]?.trim() || "",
      company_name: selectedOption?.label?.split("-")[0]?.trim() || "",
      company_type: selectedOption?.label?.split("-")[1]?.trim() || "",
    };

    setSelectedCompanyOption((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  const getCompanyOptions = (companies: any[]) => {
    return companies.map((company: any) => ({
      value: `${company.id.toString()}-${company.type}`,
      label: `${company.name} - ${company.type}`,
    }));
  };

  // EFFECTS
  useEffect(() => {
    fetchAllData();
  }, []);

  // Update UI when company changes
  useEffect(() => {
    if (selectedCompanyOption.company_id) {
      updateUI();
    }
  }, [selectedCompanyOption.company_id, selectedCompanyOption.company_type]);

  // Set default company after companies are loaded
  useEffect(() => {
    if (companies.length > 0) {
      const storedCompany = localStorage.getItem("selectedERSCompany");
      let currentData = {
        value: companies[0].id.toString(),
        label: `${companies[0].name} - ${companies[0].type}`,
      };
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        currentData = {
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        };
      }
      handleCompanySelection(currentData, setSelectedCompanyOption);
      const generateCompanyOptions = getCompanyOptions(companies);
      setCompanyOptions(generateCompanyOptions);
    }
  }, [companies]);

  // API HANDLERS
  const fetchAllData = async (): Promise<void> => {
    try {
      const [getCompaniesResponse] = await Promise.allSettled([
        api.post("userService/getAllCompanyList", {
          signature: signature,
        }),
      ]);

      const companies =
        getCompaniesResponse.status === "fulfilled"
          ? getCompaniesResponse.value.data?.data?.data || []
          : [];

      setCompanies(companies);
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load data: Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  // Fetching all data
  const fetchData = async (
    page: number = 1,
    filters: AllReportsFilters = allReportsFilters,
    loadInitialData: boolean = false
  ): Promise<void> => {
    setIsLoading(true);
    const startTime = Date.now();
    const minDuration = 300;

    try {
      const companyId = selectedCompanyOption?.company_id;
      const companyType = selectedCompanyOption?.company_type;

      if (!companyId || !companyType) {
        throw new Error("Company not selected!");
      }

      // Determine which API calls to make
      const apiCalls = [];

      if (loadInitialData) {
        // Load all initial data (report types, users, departments)
        apiCalls.push(
          api.post("ers/team-reports/getAllReportTypes", {
            signature: signature,
            company_id: parseInt(companyId),
            company_type: companyType,
          }),
          api.post("ers/team-reports/getAllUsersOfCompany", {
            signature: signature,
            company_id: parseInt(companyId),
            company_type: companyType,
          }),
          api.post("ers/team-reports/getAllDepartments", {
            signature: signature,
            company_id: parseInt(companyId),
          })
        );
      }

      // Always fetch stats and reports
      apiCalls.push(
        api.post("ers/team-reports/getTeamStatistics", {
          signature: signature,
          company_id: parseInt(companyId),
          company_type: companyType,
          start_date: filters.start_date,
          end_date: filters.end_date,
          user_id: filters?.user_id,
          report_type_id: filters?.report_type_id,
          report_frequency: filters.report_frequency,
          department_id: filters.department_id,
          search_term: filters.search_term,
        }),
        api.post("ers/team-reports/getAllTeamReports", {
          signature: signature,
          company_id: parseInt(companyId),
          company_type: companyType,
          page: page,
          limit: allReportsPaginationData.itemsPerPage,
          ...filters,
        })
      );

      const responses: any = await Promise.allSettled(apiCalls);

      let responseIndex = 0;

      // Process initial data responses if loaded
      if (loadInitialData) {
        // Report types
        if (responses[responseIndex].status === "fulfilled") {
          const reportTypesData =
            responses[responseIndex].value?.data?.data || [];
          setReportTypes(reportTypesData);
        } else {
          setReportTypes([]);
          notify.error("Failed to load report types");
        }
        responseIndex++;

        // Users
        if (responses[responseIndex].status === "fulfilled") {
          const usersData = responses[responseIndex].value?.data?.data || [];
          setAllUsers(usersData);
        } else {
          setAllUsers([]);
          notify.error("Failed to load users");
        }
        responseIndex++;

        // Departments
        if (responses[responseIndex].status === "fulfilled") {
          const departmentsData =
            responses[responseIndex].value?.data?.data || [];
          setAllDepartments(departmentsData);
        } else {
          setAllDepartments([]);
          notify.error("Failed to load departments");
        }
        responseIndex++;
      }

      // Statistics
      if (responses[responseIndex].status === "fulfilled") {
        const statsData = responses[responseIndex].value?.data?.data || {};
        setStats(statsData);
      } else {
        setStats({});
        notify.error("Failed to load statistics");
      }
      responseIndex++;

      // Reports
      if (responses[responseIndex].status === "fulfilled") {
        const reportsData = responses[responseIndex].value?.data?.data || {};
        const reports = reportsData.reports || [];
        const paginationData = reportsData.pagination || {};
        setAllReports(reports);
        setAllReportsPaginationData(paginationData);
      } else {
        setAllReports([]);
        notify.error("Failed to load reports");
      }
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to Load data: Internal Server Error!";
      }
      notify.error(errorMessage);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = minDuration - elapsed;
      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  const updateUI = async () => {
    localStorage.setItem(
      "selectedERSCompany",
      JSON.stringify(selectedCompanyOption)
    );

    setAllReportsFilters(DEFAULT_FILTERS);
    setSearchTerm(null);
    setReportFrequency(null);
    setReportTypeFilter(null);
    setReportsStartDate(null);
    setReportsEndDate(null);
    setUserFilter(null);
    setDepartmentFilter(null);
    setReportsSortBy(null);

    await fetchData(1, DEFAULT_FILTERS, true);
  };

  const handleAllReportsPageChange = (page: number): void => {
    fetchData(page);
  };

  // Stats Table Data
  const [modalInfo, setModalInfo] = useState("");
  const [modalData, setModalData] = useState<any[]>([]);
  const [modalDataColumns, setmodalDataColumns] = useState<any[]>([]);

  const total_reports_columns = [
    {
      title: "Submitted By",
      dataIndex: "user_id",
      render: (id: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-sm avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.profile_pic || record?.profile_pic === "null"
                  ? "assets/img/users/user.png"
                  : record?.profile_pic
              }
              alt="img"
            />
          </li>
          <div>
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
              {toProperCase(
                getFullName(record?.salutation, record?.fname, record?.lname)
              )}
            </h6>

            <span className="fs-12 fw-normal table-text-wrap">
              {toProperCase(record?.designation || "No Designation")}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        getFullName(a?.salutation, a?.fname, a?.lname).localeCompare(
          getFullName(b?.salutation, b?.fname, b?.lname)
        ),
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium mb-1 table-text-wrap">
          {toProperCase(text)}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.department || "").localeCompare(b?.department || ""),
    },
    {
      title: "Daily Count",
      dataIndex: "daily_count",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.daily_count - b?.daily_count,
    },
    {
      title: "Weekly Count",
      dataIndex: "weekly_count",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.weekly_count - b?.weekly_count,
    },
    {
      title: "Monthly Count",
      dataIndex: "monthly_count",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.monthly_count - b?.monthly_count,
    },
    {
      title: "Other Count",
      dataIndex: "custom_count",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.monthly_count - b?.monthly_count,
    },
    {
      title: "Total Count",
      dataIndex: "user_id",
      render: (text: string, record: any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-success py-2 p-2 table-text-wrap">
          {record?.daily_count +
            record?.weekly_count +
            record?.monthly_count +
            record?.custom_count}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        a?.daily_count +
        a?.weekly_count +
        a?.monthly_count +
        a?.custom_count -
        (b?.daily_count + b?.weekly_count + b?.monthly_count + b?.custom_count),
    },
  ];

  //Card and numbers click
  const handleStatsCardClick = (category: string) => {
    let title = "";
    let data: any[] = [];
    let columns: any = [];

    switch (category) {
      case "total-stats":
        title = "Total Submitted Reports";
        data = stats?.total_breakdown;
        columns = total_reports_columns;
        break;

      case "submitted-on-time-reports":
        title = "On-Time Submissions";
        data = stats?.submitted_on_time_breakdown;
        columns = total_reports_columns;
        break;

      case "submitted-late-reports":
        title = "Late Submissions";
        data = stats?.submitted_late_breakdown;
        columns = total_reports_columns;
        break;

      case "overdue-reports":
        title = "Overdue Reports";
        data = stats?.overdue_breakdown;
        columns = total_reports_columns;
        break;

      default:
        title = "";
        data = [];
        columns = [];
        break;
    }

    setModalInfo(title);
    setModalData(data);
    setmodalDataColumns(columns);
  };

  // Clear Filter Button Component
  const ClearFilterBtn = ({
    onClick,
    title = "Clear this filter",
  }: {
    onClick: () => void;
    title?: string;
  }) => (
    <button
      className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
      onClick={onClick}
      title={title}
      style={{ fontSize: "12px", marginLeft: "8px" }}>
      <i className="ti ti-x fs-13 me-1" />
      Clear
    </button>
  );

  // RENDER COMPONENT
  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header Section */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Team Reports</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Team Reports</li>
                  <li className="breadcrumb-item">Overview</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2" title="Choose Company">
                <CommonSelect
                  className="select  me-2"
                  options={companyOptions}
                  defaultValue={{
                    value: `${selectedCompanyOption.company_id}-${selectedCompanyOption.company_type}`,
                    label:
                      truncateCompanyText(
                        selectedCompanyOption.company_name,
                        30
                      ) +
                      "-" +
                      selectedCompanyOption.company_type,
                  }}
                  onChange={(selectedOption: any) =>
                    handleCompanySelection(
                      selectedOption,
                      setSelectedCompanyOption
                    )
                  }
                  isDisabled={!isAdmin}
                />
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="card">
            <div className="card-header border-0">
              <div className="d-flex align-items-center justify-content-between">
                <div
                  onClick={toggleFilters}
                  className="d-flex gap-3 align-items-center"
                  style={{ cursor: "pointer" }}>
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className={`me-1 fs-28 ti ti-filter`}></i>
                    Filters
                  </h5>
                  <span
                    className={`btn btn-md ${activeFiltersCount > 0
                      ? "btn-warning text-white"
                      : "btn-secondary"
                      }`}>
                    {showFilters ? "Hide" : "Show"}
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    <i
                      className={`ms-1 ti ti-caret-${showFilters ? "up" : "down"
                        }`}></i>
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    style={{ minWidth: "100px" }}
                    className="btn btn-info btn-md"
                    onClick={handleAllReportsSearch}>
                    <i className="ti ti-search me-1"></i>
                    Search
                  </button>
                  <button
                    style={{ minWidth: "100px" }}
                    className="btn btn-outline-danger btn-md"
                    onClick={handleClearAllReportsFilters}
                    disabled={activeFiltersCount === 0}>
                    <i className="ti ti-x me-1"></i>
                    Clear {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                  </button>
                </div>
              </div>

              {/* Filters Section */}
              {showFilters && (
                <div className="mt-3 d-flex flex-wrap bg-secondary bg-opacity-10 align-items-end p-3 rounded-3 gap-3">
                  {/* Search by title  */}
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Search Reports
                      </label>
                      {searchTerm && (
                        <ClearFilterBtn onClick={clearSearchTerm} />
                      )}
                    </div>
                    <Input
                      className="mb-2 p-2"
                      placeholder="Search by title..."
                      value={searchTerm || ""}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      prefix={<i className="ti ti-search" />}
                    />
                  </div>

                  {/* Date Filters  */}
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Start Date
                      </label>
                      {reportsStartDate && (
                        <ClearFilterBtn onClick={clearStartDateFilters} />
                      )}
                    </div>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={reportsStartDate}
                      onChange={(date: Dayjs | null) =>
                        setReportsStartDate(date)
                      }
                      placeholder="Start Date"
                      format="DD MMM YYYY"
                    />
                  </div>
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">End Date</label>
                      {reportsEndDate && (
                        <ClearFilterBtn onClick={clearEndDateFilters} />
                      )}
                    </div>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={reportsEndDate}
                      onChange={(date: Dayjs | null) => setReportsEndDate(date)}
                      placeholder="End Date"
                      format="DD MMM YYYY"
                    />
                  </div>

                  {/* Filter by Frequency type */}
                  <div style={{ minWidth: "180px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">Frequency</label>
                      {reportFrequency && (
                        <ClearFilterBtn onClick={clearReportFrequency} />
                      )}
                    </div>
                    <Select
                      style={{ width: "100%" }}
                      className="mb-2"
                      placeholder="Select Frequency"
                      value={reportFrequency}
                      onChange={(value) => setReportFrequency(value)}
                      options={[
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                        { label: "Monthly", value: "monthly" },
                      ]}
                    />
                  </div>

                  {/* Sort */}
                  <div style={{ minWidth: "250px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">Sort By</label>
                      {reportsSortBy && (
                        <ClearFilterBtn onClick={clearSortBy} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Sort By"
                      value={reportsSortBy}
                      onChange={(value) => setReportsSortBy(value)}
                      options={reportsSortOptions}
                    />
                  </div>

                  {/* Filter by Report type  */}
                  <div style={{ minWidth: "200px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Report Type
                      </label>
                      {reportTypeFilter && (
                        <ClearFilterBtn onClick={clearReportTypeFilter} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Select Report"
                      value={reportTypeFilter}
                      onChange={(value) => setReportTypeFilter(value)}
                      showSearch
                      filterOption={(input: any, option: any) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={reportTypes?.map((reportType: any) => ({
                        value: reportType.id,
                        label: reportType.name,
                      }))}
                    />
                  </div>

                  {/* Filter by user */}
                  <div style={{ minWidth: "250px" }} >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">Employee</label>
                      {userFilter && (
                        <ClearFilterBtn onClick={clearUserFilter} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Select User"
                      value={userFilter}
                      onChange={(value) => setUserFilter(value)}
                      showSearch
                      filterOption={(input: any, option: any) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={getEmployeeOptions(allUsers)}
                      maxTagCount="responsive"
                      optionRender={(option) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "0",
                          }}>
                          <img
                            src={
                              option.data.image_url ||
                              "assets/img/users/user.png"
                            }
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {option.label}
                          </span>
                        </div>
                      )}
                    />
                  </div>

                  {/* Filter by Department */}
                  <div style={{ minWidth: "200px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Department
                      </label>
                      {departmentFilter && (
                        <ClearFilterBtn onClick={clearDepartmentFilter} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Select Department"
                      value={departmentFilter}
                      onChange={(value) => setDepartmentFilter(value)}
                      showSearch
                      filterOption={(input: any, option: any) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={allDepartments?.map((dept: any) => ({
                        value: dept.id,
                        label: dept.name,
                      }))}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Top Row - Stats*/}
          <div className="pt-0 d-flex flex-wrap align-items-center justify-content-between bg-none">
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={() => handleStatsCardClick("total-stats")}>
                <StatsCard
                  icon="ti-report-analytics"
                  iconColor="text-info"
                  title="Total Submitted"
                  value={stats?.summary?.total_submitted || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={() =>
                  handleStatsCardClick("submitted-on-time-reports")
                }>
                <StatsCard
                  icon="ti-circle-check"
                  iconColor="text-success"
                  title="Submitted On-Time"
                  value={stats?.summary?.submitted_on_time || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={() => handleStatsCardClick("submitted-late-reports")}>
                <StatsCard
                  icon="ti-files"
                  iconColor="text-primary"
                  title="Submitted Late"
                  value={stats?.summary?.submitted_late || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={() => handleStatsCardClick("overdue-reports")}>
                <StatsCard
                  icon="ti-alert-triangle"
                  iconColor="text-danger"
                  title="Due Reports"
                  value={stats?.summary?.overdue_reports || 0}
                  className="h-100"
                />
              </Link>
            </div>
          </div>

          {/* Bottom Section - All Reports */}
          <div className="card mt-3">
            <div className="card-header border-0">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  <i className="ti ti-files text-primary me-2"></i>
                  All Reports
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge badge-md bg-primary">
                    {allReportsPaginationData.totalItems} Total
                  </span>
                </div>
              </div>
            </div>

            <div className="card-body">
              {allReports.length > 0 ? (
                <>
                  <div className="reports-list">
                    {allReports.map((report) => (
                      <AllReportsCard key={report.id} report={report} />
                    ))}
                  </div>

                  {allReportsPaginationData.totalPages >= 1 && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <Pagination
                          currentPage={allReportsPaginationData.currentPage}
                          totalPages={allReportsPaginationData.totalPages}
                          totalItems={allReportsPaginationData.totalItems}
                          itemsPerPage={allReportsPaginationData.itemsPerPage}
                          onPageChange={handleAllReportsPageChange}
                          size="sm"
                          showInfo={true}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i
                    className="ti ti-file-text text-muted"
                    style={{ fontSize: "3rem" }}></i>
                  <h5 className="mt-3 mb-2">No Reports Found</h5>
                  {Object.values(allReportsFilters).some((f) => f !== null) && (
                    <div className="d-flex gap-3 align-items-center justify-content-center">
                      <h5> "Try adjusting your filters to see more results"</h5>
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleClearAllReportsFilters}>
                        <i className="ti ti-filter-off me-2"></i>
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-4">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <QuickInfoModal
        title={modalInfo}
        dataSource={modalData}
        columns={modalDataColumns}
      />
    </>
  );
};

export default TeamReports;
